import React, { useState, useEffect } from "react";
import Select from "react-select"; 
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import TaskJDModal from "./TaskJdModal"; 
import "./TaskJDList.css"; 

const PAGE_SIZE = 10;

const TaskJDList = () => {
  const [taskJDs, setTaskJDs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filterUser, setFilterUser] = useState(null); // Sử dụng object cho react-select
  const [filterDepts, setFilterDepts] = useState([]); 
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async () => {
    try {
      const [resJD, resDept, resUser] = await Promise.all([
        axiosClient.get(API_ENDPOINTS.taskjds),
        axiosClient.get(API_ENDPOINTS.departments),
        axiosClient.get(API_ENDPOINTS.users)
      ]);
      
      setTaskJDs(Array.isArray(resJD.data) ? resJD.data : (resJD.data?.data || []));
      setDepartments(Array.isArray(resDept.data) ? resDept.data : (resDept.data?.data || []));
      setUsers(Array.isArray(resUser.data) ? resUser.data : (resUser.data?.data || []));
    } catch (error) {
      console.error("❌ Data load error:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- LOGIC IMPORT / EXPORT / DELETE ALL ---
  const handleExport = async () => {
    try {
      const response = await axiosClient.get(`${API_ENDPOINTS.taskjds}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `TaskJDs_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { alert("Export failed!"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("File", file); 
    try {
      const response = await axiosClient.post(`${API_ENDPOINTS.taskjds}/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.success) {
        alert(`Successfully imported ${response.data.inserted} records!`);
        fetchData();
      } else {
        alert("Import failed: " + (response.data.error || "Unknown error"));
      }
      e.target.value = null;
    } catch (error) { 
      alert("Import failed!"); 
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("WARNING: Are you sure you want to delete ALL data?")) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.taskjds}/delete-all`);
      alert("All data has been cleared!");
      fetchData();
      setCurrentPage(1);
    } catch (error) { alert("Delete all failed!"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const res = await axiosClient.delete(`${API_ENDPOINTS.taskjds}/${id}`);
      if (res.data && res.data.success) { 
        alert(res.data.message); 
        fetchData(); 
      }
    } catch (error) { 
      const errorMsg = error.response?.data?.message || "Error deleting task.";
      alert(errorMsg); 
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingTask) await axiosClient.put(`${API_ENDPOINTS.taskjds}/${editingTask.id}`, data);
      else await axiosClient.post(API_ENDPOINTS.taskjds, data);
      fetchData();
      setShowModal(false);
    } catch (error) { 
      const errorMsg = error.response?.data?.message || "Error saving!";
      alert(errorMsg); 
    }
  };

  // --- LOGIC LỌC DỮ LIỆU ---
  const filteredData = taskJDs.filter((t) => {
    const mainTaskList = t.mainTaskList || "";
    const fullName = t.fullName || t.userName || ""; 
    
    const matchesSearch = mainTaskList.toLowerCase().includes(search.toLowerCase()) ||
                          fullName.toLowerCase().includes(search.toLowerCase());
    
    const matchesUser = !filterUser || String(t.userId) === String(filterUser.value);

    const selectedDeptNames = filterDepts.map(d => d.label);
    const matchesDept = selectedDeptNames.length === 0 || selectedDeptNames.includes(t.departmentName);

    return matchesSearch && matchesUser && matchesDept;
  });

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const currentData = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const thStyle = { backgroundColor: "#f8f9fc", color: "#4e73df", fontWeight: "bold", fontSize: "0.8rem", textTransform: "uppercase" };

  const deptOptions = departments.map(d => ({ value: d.id, label: d.name }));
  const userOptions = users.map(u => ({ value: u.id, label: u.fullName || u.userName }));

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h5 className="m-0 font-weight-bold text-dark text-uppercase" style={{fontSize: "1rem"}}>Task JD Management</h5>
          
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div style={{ minWidth: "200px" }}>
              <Select
                isMulti
                options={deptOptions}
                placeholder="-- Departments --"
                value={filterDepts}
                onChange={(selected) => { setFilterDepts(selected || []); setCurrentPage(1); }}
                classNamePrefix="select"
                styles={{
                  control: (base) => ({ ...base, borderRadius: "20px", fontSize: "0.8rem", minHeight: "31px" }),
                  valueContainer: (base) => ({ ...base, padding: "0 6px" })
                }}
              />
            </div>

            <div style={{ minWidth: "200px" }}>
              <Select
                isClearable
                options={userOptions}
                placeholder="-- Select Full Name --"
                value={filterUser}
                onChange={(selected) => { setFilterUser(selected); setCurrentPage(1); }}
                classNamePrefix="select"
                styles={{
                  control: (base) => ({ ...base, borderRadius: "20px", fontSize: "0.8rem", minHeight: "31px" }),
                  valueContainer: (base) => ({ ...base, padding: "0 6px" })
                }}
              />
            </div>

            <div className="position-relative">
               <input
                type="text"
                className="form-control form-control-sm ps-4"
                style={{ width: "180px", borderRadius: "20px" }}
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              />
              <i className="fas fa-search position-absolute text-muted" style={{ left: "12px", top: "8px", fontSize: "11px" }}></i>
            </div>

            <div className="btn-group shadow-sm">
              <button className="btn btn-sm btn-light border" onClick={handleExport} title="Export">
                <i className="fas fa-file-export text-primary"></i>
              </button>
              <label className="btn btn-sm btn-light border mb-0" style={{ cursor: "pointer" }}>
                <i className="fas fa-file-import text-warning"></i> 
                <input type="file" accept=".xlsx" hidden onChange={handleImport} />
              </label>
             
            </div>

            <button className="btn btn-sm btn-primary px-3 rounded-pill shadow-sm fw-bold" onClick={() => { setEditingTask(null); setShowModal(true); }}>
              <i className="fas fa-plus me-1"></i> Add
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{fontSize: "0.85rem"}}>
              <thead>
                <tr>
                  <th className="text-center" style={thStyle}>No.</th>
                  <th style={thStyle}>Main JD</th>
                  <th style={thStyle}>Main Task List</th>
                  <th style={thStyle}>Assignee</th>
                  <th style={thStyle}>Department</th>
                  <th className="text-center" style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? currentData.map((t, idx) => (
                  <tr key={t.id}>
                    <td className="text-center text-muted">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                    <td><div className="text-muted text-truncate" style={{maxWidth: "250px"}} title={t.mainJD}>{t.mainJD}</div></td>
                    <td><div className="fw-bold text-primary text-truncate" style={{maxWidth: "200px"}} title={t.mainTaskList}>{t.mainTaskList}</div></td>
                    <td><span className="fw-bold text-dark">{t.userName || "Unassigned"}</span></td>
                    <td><small>{t.departmentName}</small></td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2">
                        <button className="btn btn-sm btn-outline-primary border-0 rounded-circle" onClick={() => { setEditingTask(t); setShowModal(true); }}>
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger border-0 rounded-circle" onClick={() => handleDelete(t.id)}>
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="6" className="text-center py-5 text-muted">No Task JD found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 py-3">
            <nav>
              <ul className="pagination pagination-sm justify-content-center mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      <TaskJDModal 
        show={showModal} 
        initialData={editingTask} 
        departments={departments} 
        users={users} 
        onClose={() => { setShowModal(false); setEditingTask(null); }} 
        onSave={handleSave} 
      />
    </div>
  );
};

export default TaskJDList;